import { useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useAskQuestion, useListChatMessages, useSubmitAnswerFeedback } from "../api";
import { ChatContext } from "../context/ChatContextProvider";
import { AnswerFeedbackScore } from "../types";

export const useChat = () => {
  const context = useContext(ChatContext);
  const askQuestionMutation = useAskQuestion();
  const submitAnswerFeedbackMutation = useSubmitAnswerFeedback();
  const history = useHistory();
  const { chatId: urlChatId } = useParams<{ chatId?: string }>();

  const listChatMessages = useListChatMessages(urlChatId);

  if (!context) {
    throw new Error("useChat must be used within a ChatContextProvider");
  }

  const askQuestion = async (question: string, warcEnabled: boolean) => {
    context.setPendingQuestion(question);

    try {
      return await askQuestionMutation.mutateAsync(
        {
          question,
          ...(urlChatId ? { chat_id: urlChatId } : {}),
          documentIds: [...context.enabledDocumentIds],
          model: context.selectedModel as "CLAUDE" | "GEMINI" | undefined,
          warcEnabled,
        },
        {
          onSuccess(result) {
            listChatMessages.insertOptimistically(result);

            if (!urlChatId) {
              history.push(`/maite/${result.data.chatId}`);
            }
          },
          onSettled() {
            if (urlChatId) {
              listChatMessages.refetch();
            }
          },
          onError(error) {
            toast.error("Something went wrong, please try again");
            throw error;
          },
        }
      );
    } finally {
      context.setPendingQuestion(undefined);
      context.setEnabledDocumentIds([]);
    }
  };

  const scoreAnswer = (messageId: string, score: AnswerFeedbackScore) => {
    if (!urlChatId) {
      return;
    }

    return submitAnswerFeedbackMutation.mutateAsync(
      {
        chatId: urlChatId,
        messageId,
        score: score === "positive" ? 1 : -1,
        modelUsed: (context.selectedModel ?? "CLAUDE") as "CLAUDE" | "GEMINI",
      },
      {
        onSuccess(result) {
          listChatMessages.updateFeedbackOptimistically(result);
        },
        onSettled() {
          listChatMessages.refetch();
        },
      }
    );
  };

  const clearChat = () => {
    history.push("/maite");
  };

  return {
    pendingQuestion: context.pendingQuestion,
    askQuestion,
    clearChat,
    scoreAnswer,
    isScoring: submitAnswerFeedbackMutation.isPending,
    chatMessages: listChatMessages.data || [],
    isInitiallyLoadingMessages: listChatMessages.isLoading,
    enabledDocumentIds: context.enabledDocumentIds,
    setEnabledDocumentIds: context.setEnabledDocumentIds,
  };
};
